import { useCallback, useEffect, useState } from 'react';
import type {
  AccHub, AccModellVorschlag, AccProjektEintrag, AccStand, AccVersion, AccVorschlaege, ApiClient,
} from '../../api';
import {
  ConfirmDialog, ContentSection, DataTable, ErrorState, Feld, LoadingState, StatusBadge, type Zustand,
} from '../../muster';
import { accFehlertext, versionText } from './AccDurchsuchen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * „ZUORDNUNG“ (AC1c) - Vorschlag mit Begründung, dann Bestätigung mit Grund
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ACC-Projekt ↔ TGAPlan-Projekt und Modell ↔ ACC-Datei. Vorschläge schreiben nichts; „sicher“ (gleiche Modell-GUID)
 * ist vorausgewählt, „prüfen“ (nur der Name) nicht. Der Server prüft beim Bestätigen jede Datei erneut in ACC und
 * schreibt alles oder nichts.
 */

const STATUS: Record<string, { zustand: Zustand; text: string }> = {
  zugeordnet: { zustand: 'gut', text: 'zugeordnet' },
  sicher: { zustand: 'gut', text: 'sicher' },
  pruefen: { zustand: 'warnung', text: 'prüfen' },
  mehrdeutig: { zustand: 'warnung', text: 'mehrdeutig' },
  kein_treffer: { zustand: 'neutral', text: 'kein Treffer' },
  nicht_gefunden: { zustand: 'fehler', text: 'nicht gefunden' },
};

export function statusMarke(status: string): { zustand: Zustand; text: string } {
  return STATUS[status] ?? { zustand: 'neutral', text: status };
}

/** Wählbar sind nur Vorschläge mit genau einem Treffer, vorausgewählt nur „sicher“. */
export function waehlbar(v: AccModellVorschlag): boolean {
  return v.treffer !== null && (v.status === 'sicher' || v.status === 'pruefen');
}

export interface Rueckfrage {
  titel: string;
  text: string;
  bestaetigen: string;
  aktion: (grund: string) => Promise<unknown>;
}

export function AccZuordnung({ api, projektId, gewaehlt }: {
  api: ApiClient;
  projektId: string;
  /** Die Wahl aus „ACC durchsuchen“. */
  gewaehlt?: { hub: AccHub; projekt: AccProjektEintrag } | null;
}) {
  const [stand, setStand] = useState<AccStand | null>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [vorschlaege, setVorschlaege] = useState<AccVorschlaege | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<Record<string, boolean>>({});
  const [neuAuswahl, setNeuAuswahl] = useState<Record<string, boolean>>({});
  const [revitVersion, setRevitVersion] = useState<number>(2025);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);

  const standLaden = useCallback(() => api.accStand(projektId).then(setStand).catch(setLadefehler), [api, projektId]);
  useEffect(() => { standLaden(); }, [standLaden]);

  const vorschlaegeLaden = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const v = await api.accVorschlaege(projektId);
      setVorschlaege(v);
      if (v.art === 'modelle') {
        setAuswahl(Object.fromEntries(v.modelle.filter((m) => m.status === 'sicher' && m.treffer).map((m) => [m.modelId, true])));
        setNeuAuswahl({});
      }
    } catch (e) {
      setFehler(accFehlertext(e));
    } finally {
      setLaeuft(false);
    }
  };

  const nachher = async (text: string) => {
    setRueckfrage(null);
    setMeldung(text);
    await standLaden();
    if (vorschlaege) await vorschlaegeLaden();
  };

  if (ladefehler) return <ContentSection titel="Zuordnung"><ErrorState meldung="Der Stand der Zuordnung konnte nicht geladen werden." technisch={ladefehler} /></ContentSection>;
  if (!stand) return <ContentSection titel="Zuordnung"><LoadingState zeilen={3} spalten={3} /></ContentSection>;

  const p = stand.projekt;
  const anderesGewaehlt = gewaehlt && gewaehlt.projekt.id !== p.accProjectId;

  const projektZuordnen = (hub: AccHub, projekt: AccProjektEintrag) => setRueckfrage({
    titel: `„${projekt.name}“ zuordnen?`,
    text: `Das ACC-Projekt „${projekt.name}“ (Hub ${hub.name}${hub.region ? `, Region ${hub.region}` : ''}) wird diesem `
      + 'TGAPlan-Projekt zugeordnet. Die Region übernimmt TGAPlan aus dem Hub.',
    bestaetigen: 'Zuordnen',
    aktion: async (grund) => {
      await api.accProjektZuordnen(projektId, { hubId: hub.id, projektId: projekt.id, grund });
      await nachher('ACC-Projekt zugeordnet.');
    },
  });

  const modelleZuordnen = (liste: AccModellVorschlag[]) => setRueckfrage({
    titel: `${liste.length} Zuordnung(en) bestätigen?`,
    text: liste.map((m) => `${m.name} → ${m.treffer?.name ?? ''}`).join(' · '),
    bestaetigen: 'Bestätigen',
    aktion: async (grund) => {
      const r = await api.accZuordnen(projektId, liste.map((m) => ({ modelId: m.modelId, itemId: m.treffer!.itemId! })), grund);
      await nachher(`${r.zugeordnet} Modell(e) zugeordnet.`);
    },
  });

  const uebernehmen = (liste: AccVersion[]) => setRueckfrage({
    titel: `${liste.length} Datei(en) als Modell übernehmen?`,
    text: `${liste.map((d) => d.name).join(', ')} - Quelle ACC, Revit ${revitVersion}. Die Agenten öffnen sie über den Cloud-Pfad.`,
    bestaetigen: 'Übernehmen',
    aktion: async (grund) => {
      const r = await api.accUebernehmen(projektId, liste.map((d) => ({ itemId: d.itemId!, revitVersion })), grund);
      await nachher(`${r.angelegt.length} Modell(e) angelegt.`);
    },
  });

  return (
    <ContentSection
      titel="Zuordnung"
      className="acc-zuordnung"
      beschreibung="Welches ACC-Projekt zu diesem Projekt gehört und welches Modell zu welcher Datei in ACC."
      aktionen={<button className="m-knopf" onClick={vorschlaegeLaden} disabled={laeuft}>Vorschläge laden</button>}
    >
      <p data-projekt>
        {p.zugeordnet
          ? <>ACC-Projekt <strong>{p.accProjektName ?? p.accProjectId}</strong>{p.accRegion ? ` (Region ${p.accRegion})` : ''}{' '}
              <button className="m-knopf" onClick={() => setRueckfrage({
                titel: 'Projektzuordnung lösen?', text: 'Nur möglich, wenn kein Modell mehr einer ACC-Datei zugeordnet ist. Die Region bleibt.',
                bestaetigen: 'Lösen',
                aktion: async (grund) => { await api.accProjektLoesen(projektId, grund); await nachher('Projektzuordnung gelöst.'); },
              })}>Lösen …</button></>
          : 'Noch keinem ACC-Projekt zugeordnet.'}
      </p>
      {anderesGewaehlt && (
        <p data-gewaehlt>
          <button className="m-knopf m-knopf-haupt" onClick={() => projektZuordnen(gewaehlt.hub, gewaehlt.projekt)}>
            „{gewaehlt.projekt.name}“ zuordnen …
          </button>
        </p>
      )}
      {fehler && <ErrorState klein meldung={fehler} />}
      {meldung && <p role="status">{meldung}</p>}

      {vorschlaege?.art === 'projekt' && (
        vorschlaege.projektVorschlaege.length === 0
          ? <p className="sehr-schwach" data-projektvorschlaege>Kein Vorschlag - bitte unter „ACC durchsuchen“ wählen.</p>
          : (
            <ul data-projektvorschlaege>
              {vorschlaege.projektVorschlaege.map((x) => (
                <li key={x.projekt.id}>
                  <StatusBadge {...statusMarke(x.status)} /> <strong>{x.projekt.name}</strong> - {x.grund}{' '}
                  <button className="m-knopf" onClick={() => projektZuordnen(x.hub, x.projekt)}>Zuordnen …</button>
                </li>
              ))}
            </ul>
          )
      )}

      {vorschlaege?.art === 'modelle' && (
        <>
          <div data-modellvorschlaege>
            <DataTable<AccModellVorschlag>
              name="Vorschläge je Modell"
              zeilen={vorschlaege.modelle}
              schluessel={(m) => m.modelId}
              spalten={[
                {
                  schluessel: 'wahl', titel: 'Wahl', breite: '4em',
                  inhalt: (m) => (waehlbar(m) ? (
                    <input type="checkbox" aria-label={`${m.name} zuordnen`} checked={!!auswahl[m.modelId]}
                           onChange={(e) => setAuswahl((a) => ({ ...a, [m.modelId]: e.target.checked }))} />
                  ) : null),
                },
                { schluessel: 'name', titel: 'Modell', wert: (m) => m.name },
                { schluessel: 'status', titel: 'Vorschlag', wert: (m) => m.status, inhalt: (m) => <StatusBadge {...statusMarke(m.status)} /> },
                {
                  schluessel: 'datei', titel: 'Datei in ACC',
                  wert: (m) => (m.treffer ? `${m.treffer.name ?? ''} · ${versionText(m.treffer)}` : ''),
                },
                { schluessel: 'grund', titel: 'Begründung', wert: (m) => m.grund },
              ]}
            />
          </div>
          <button className="m-knopf m-knopf-haupt" data-bestaetigen
                  disabled={!vorschlaege.modelle.some((m) => waehlbar(m) && auswahl[m.modelId])}
                  onClick={() => modelleZuordnen(vorschlaege.modelle.filter((m) => waehlbar(m) && auswahl[m.modelId]))}>
            Auswahl bestätigen …
          </button>
          {vorschlaege.neu.length > 0 && (
            <div data-neu>
              <h4>Neu in ACC (keinem Modell zugeordnet)</h4>
              <ul>
                {vorschlaege.neu.map((d) => (
                  <li key={d.itemId ?? d.id}>
                    <label>
                      <input type="checkbox" checked={!!neuAuswahl[d.itemId ?? '']}
                             onChange={(e) => setNeuAuswahl((a) => ({ ...a, [d.itemId ?? '']: e.target.checked }))} />
                      {' '}{d.name} · {versionText(d)}
                    </label>
                  </li>
                ))}
              </ul>
              <Feld art="zahl" beschriftung="Revit-Version" wert={revitVersion} min={2019} max={2035}
                    beiAenderung={(w) => setRevitVersion(Number(w) || 2025)} />
              <button className="m-knopf" disabled={!vorschlaege.neu.some((d) => neuAuswahl[d.itemId ?? ''])}
                      onClick={() => uebernehmen(vorschlaege.neu.filter((d) => neuAuswahl[d.itemId ?? '']))}>
                Als Modell übernehmen …
              </button>
            </div>
          )}
        </>
      )}

      {stand.modelle.some((m) => m.accItemUrn) && (
        <ul className="acc-zugeordnet" data-zugeordnet>
          {stand.modelle.filter((m) => m.accItemUrn).map((m) => (
            <li key={m.id} data-zugeordnet-modell={m.name}>
              {m.name} · V{m.accVersionNummer ?? '?'}{m.accVersionQuittiert !== null && m.accVersionQuittiert !== m.accVersionNummer
                ? ` (bekannt bis V${m.accVersionQuittiert})` : ''}{' '}
              <button className="m-knopf" onClick={() => setRueckfrage({
                titel: `Zuordnung von „${m.name}“ lösen?`,
                text: 'Die Kennungen für den Cloud-Pfad bleiben; die Agenten öffnen das Modell weiter.',
                bestaetigen: 'Lösen',
                aktion: async (grund) => { await api.accModellLoesen(projektId, m.id, grund); await nachher(`Zuordnung von ${m.name} gelöst.`); },
              })}>Lösen …</button>
            </li>
          ))}
        </ul>
      )}

      <GrundDialog rueckfrage={rueckfrage} beiAbbrechen={() => setRueckfrage(null)} />
    </ContentSection>
  );
}

export function GrundDialog({ rueckfrage, beiAbbrechen }: { rueckfrage: Rueckfrage | null; beiAbbrechen: () => void }) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => { setGrund(''); setFehler(null); setLaeuft(false); }, [rueckfrage]);

  return (
    <ConfirmDialog
      offen={rueckfrage !== null}
      titel={rueckfrage?.titel ?? ''}
      text={rueckfrage?.text}
      bestaetigenText={rueckfrage?.bestaetigen ?? 'OK'}
      laeuft={laeuft}
      fehler={fehler}
      beiAbbrechen={beiAbbrechen}
      beiBestaetigen={async () => {
        if (!rueckfrage) return;
        if (grund.trim().length < 3) { setFehler(new Error('Bitte einen Grund angeben (mindestens 3 Zeichen).')); return; }
        setLaeuft(true);
        setFehler(null);
        try {
          await rueckfrage.aktion(grund.trim());
        } catch (e) {
          setFehler(new Error(accFehlertext(e)));
          setLaeuft(false);
        }
      }}
    >
      <Feld art="textfeld" beschriftung="Grund" wert={grund} beiAenderung={(w) => setGrund(w as string)}
            zeilen={2} pflicht breite="voll" />
    </ConfirmDialog>
  );
}
